import { OpusAgentChatTurnStatus } from "@src/types.generated";
import type { OpusAgentEventLike, OpusAgentQuestionLike } from "./events";

// Picked option indexes by question id, ascending (the question's option
// order). Indexes, not labels, so duplicate labels stay distinct.
export type QuestionAnswers = Readonly<Record<string, readonly number[]>>;

// open: the chat's latest questions, no message sent after them.
// answered: a later message followed them (Continue, typed or spoken).
// replaced: a later questions event in the same turn supersedes them, with
// no message between (a later turn would be a reply, so "answered").
// closed: the latest, unanswered, in a chat that can't send.
export type QuestionsStatus = "open" | "answered" | "replaced" | "closed";

export interface QuestionsState {
  eventId: string;
  turnId: string;
  questions: readonly OpusAgentQuestionLike[];
  status: QuestionsStatus;
  // Chips and Continue are offered: open, and the asking turn has stopped.
  actionable: boolean;
  // The message that followed (status "answered").
  reply: string | null;
}

type TurnLike = {
  id: string;
  userMessage: string;
  status: OpusAgentChatTurnStatus;
  pending?: boolean;
  events: (OpusAgentEventLike & { id: string })[];
};

/**
 * Every questions event in the chat, keyed by event id, with its status.
 * Same rule as plans (planProposals.ts): only the latest can be answered,
 * and only until a message follows it.
 */
export const questionsStates = (
  turns: readonly TurnLike[],
  { canSend = true }: { canSend?: boolean } = {},
): Map<string, QuestionsState> => {
  const found: {
    eventId: string;
    turnIndex: number;
    questions: readonly OpusAgentQuestionLike[];
  }[] = [];
  turns.forEach((turn, turnIndex) => {
    for (const event of [...turn.events].sort((a, b) => a.index - b.index)) {
      const asked = event.payload.questions;
      if (asked)
        found.push({
          eventId: event.id,
          turnIndex,
          questions: asked.questions,
        });
    }
  });
  const states = new Map<string, QuestionsState>();
  found.forEach(({ eventId, turnIndex, questions }, position) => {
    const turn = turns[turnIndex];
    const reply = turns[turnIndex + 1];
    const isLatest = position === found.length - 1;
    const status: QuestionsStatus = reply
      ? "answered"
      : !isLatest
        ? "replaced"
        : canSend
          ? "open"
          : "closed";
    states.set(eventId, {
      eventId,
      turnId: turn.id,
      questions,
      status,
      actionable:
        status === "open" &&
        !turn.pending &&
        turn.status !== OpusAgentChatTurnStatus.Running,
      reply: reply ? reply.userMessage : null,
    });
  });
  return states;
};

// Typed "Something else…" answers by question id. A non-empty one is that
// question's answer, in place of any picked options.
export type QuestionTexts = Readonly<Record<string, string>>;

// One question's answer text: the typed answer when there is one (the card
// keeps picks empty while it is), else the
// picked option labels joined by ", "; null when unanswered.
export const answerText = (
  question: OpusAgentQuestionLike,
  answers: QuestionAnswers,
  texts: QuestionTexts = {},
): string | null => {
  const typed = texts[question.id]?.trim();
  if (typed) return typed;
  const picked = question.options.filter((_, index) =>
    answers[question.id]?.includes(index),
  );
  return picked.length ? picked.join(", ") : null;
};

/**
 * The Continue message (the scadmin questions contract): one line per
 * answered question, `<question text> <answer>`, joined by newline, where
 * the answer is the typed text or the picked labels joined by ", ".
 * Unanswered questions are left out.
 */
export const composeAnswers = (
  questions: readonly OpusAgentQuestionLike[],
  answers: QuestionAnswers,
  texts: QuestionTexts = {},
): string =>
  questions
    .map((question) => {
      const answer = answerText(question, answers, texts);
      return answer ? `${question.text} ${answer}` : null;
    })
    .filter((line): line is string => line !== null)
    .join("\n");

// A chip tap: single-select picks only this one (tapping the picked one
// keeps it, like a native radio); multi-select adds or removes it.
export const toggleAnswer = (
  answers: QuestionAnswers,
  question: OpusAgentQuestionLike,
  optionIndex: number,
): QuestionAnswers => {
  const current = answers[question.id] ?? [];
  const picked = current.includes(optionIndex);
  const next = question.multiSelect
    ? picked
      ? current.filter((value) => value !== optionIndex)
      : [...current, optionIndex].sort((a, b) => a - b)
    : [optionIndex];
  return { ...answers, [question.id]: next };
};

/**
 * What the reply said for each question, when it reads in the composed
 * form (`<question text> <answer>` on its own line): question id → answer
 * text. A typed or spoken answer in other words gives an empty map.
 * Longest question text first, each line used once, so a question whose
 * text is a prefix of another's ("Who?" / "Who? Which shift?") never takes
 * the other's line.
 */
export const answersFromReply = (
  questions: readonly OpusAgentQuestionLike[],
  reply: string | null,
): Map<string, string> => {
  const found = new Map<string, string>();
  if (!reply) return found;
  const lines: (string | null)[] = reply.split("\n").map((line) => line.trim());
  const longestFirst = [...questions].sort(
    (a, b) => b.text.trim().length - a.text.trim().length,
  );
  for (const question of longestFirst) {
    const prefix = `${question.text.trim()} `;
    const at = lines.findIndex(
      (candidate) =>
        candidate !== null &&
        candidate.startsWith(prefix) &&
        candidate.slice(prefix.length).trim() !== "",
    );
    if (at === -1) continue;
    found.set(question.id, lines[at]!.slice(prefix.length).trim());
    lines[at] = null;
  }
  return found;
};

export const picksFileOption = (
  questions: readonly OpusAgentQuestionLike[],
  answers: QuestionAnswers,
): boolean =>
  questions.some((question) =>
    question.options.some(
      (option, index) =>
        answers[question.id]?.includes(index) &&
        /\b(attach|upload)/i.test(option),
    ),
  );

export const promptNeedsFile = (prompt: string): boolean =>
  /\bthis (sop|file|doc|document|deck|pdf)\b/i.test(prompt);
